import * as htmlToImage from "html-to-image";
import { calculateCanvasHeight, wrapText } from "../../../hooks/useDownloadChart";
import { sendAnalyticsEvent } from "../../../FirebaseConfig";
import { API_FILES_BASE_URL } from "../../../utils/const";
import { CapaMapa } from "../../../interfaces/Indicator";

// Caché compartido a nivel de módulo — evita pedir la misma imagen
// más de una vez cuando varias capas/markers usan el mismo ícono
const imageCache = new Map<string, Promise<HTMLImageElement>>();

const loadImage = (src: string): Promise<HTMLImageElement> => {
  if (imageCache.has(src)) {
    return imageCache.get(src)!;
  }

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

const drawLayerReferences = async (
  context: CanvasRenderingContext2D,
  capasMapa: CapaMapa[],
  activeCapaIds: number[],
  startX: number,
  startY: number,
  maxWidth: number,
  lineHeight: number
): Promise<number> => {
  const capasActivas = capasMapa.filter((c) => activeCapaIds.includes(c.id));
  if (!capasActivas.length) return startY;

  let contentHeight = startY;
  const iconSize = 16;
  const gap = 6;

  context.font = "bold 14px Roboto";
  context.fillStyle = "#000";
  context.textAlign = "left";
  context.fillText("Capas:", startX, contentHeight);
  contentHeight += lineHeight;

  const iconsWithNames = await Promise.all(
    capasActivas.map(async (capa) => {
      try {
        const img = await loadImage(`${API_FILES_BASE_URL}${capa.imagen.replace("files/", "")}`);
        return { img, nombre: capa.nombre };
      } catch {
        return { img: null, nombre: capa.nombre };
      }
    })
  );

  iconsWithNames.forEach(({ img, nombre }) => {
    if (img) {
      context.drawImage(img, startX, contentHeight - iconSize + 4, iconSize, iconSize);
    }
    context.font = "14px Roboto";
    context.fillStyle = "#000";
    context.fillText(nombre, startX + iconSize + gap, contentHeight);
    contentHeight += lineHeight;
  });

  return contentHeight + 10;
};

export const handleDownloadMap = (
  nodeRef: React.RefObject<HTMLDivElement>,
  name: string,
  filtersSelected: { [key: string]: string[] },
  breadcrumbs: string,
  name_indicator: string,
  note: string,
  source: string,
  format: "png" | "jpg",
  capasMapa: CapaMapa[] = [],
  activeCapaIds: number[] = []
) => {
  const node = nodeRef.current;
  const margin = 20;

  if (node) {
    node.style.position = "relative";
    const scale = 1;

    htmlToImage
      .toCanvas(node, {
        width: (node.offsetWidth) * scale,
        height: node.offsetHeight * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          width: `${node.offsetWidth}px`,
          height: `${node.offsetHeight}px`,
        },
        filter: (domNode) => {
          if (domNode instanceof HTMLElement && domNode.dataset?.excludeFromCapture === "true") {
            return false;
          }
          return true;
        },
      })
      .then(async (canvas) => {

        const newCanvas = document.createElement("canvas");
        const context = newCanvas.getContext("2d");
        const lineHeight = 20;

        newCanvas.width = canvas.width;
        newCanvas.height = calculateCanvasHeight(
          breadcrumbs,
          filtersSelected,
          [],
          "",
          canvas.height,
          context!,
          margin,
          note,
          0,
          source
        ) + margin * 2 + (activeCapaIds.length > 0 ? (activeCapaIds.length + 1) * lineHeight + 20 : 0);

        if (context) {

          if (format === 'jpg') {
            context.fillStyle = "#FFFFFF";
            context.fillRect(0, 0, canvas.width, newCanvas.height);
          }

          let contentHeight = margin + 20;

          // Dibuja el breadcrumb en la parte superior del canvas
          context.font = "14px Roboto";
          context.fillStyle = "#000";
          context.textAlign = "left";

          // Ajusta contentHeight con la altura real del breadcrumb
          const breadcrumbHeight = wrapText(
            context,
            `${breadcrumbs.replaceAll(">", " / ")} / ${name_indicator}`,
            margin,
            contentHeight,
            canvas.width - margin * 2,
            lineHeight
          );

          contentHeight += breadcrumbHeight + 10; // Agregar espacio adicional antes del título

          // titulo
          context.font = "bold 20px Roboto";
          context.fillStyle = "#000";
          context.textAlign = "left";

          const titleHeight = wrapText(
            context,
            name,
            margin,
            contentHeight,
            canvas.width - margin * 2,
            lineHeight
          );
          contentHeight += titleHeight + 10; // Añade espacio debajo del título

          // Dibuja los filtros seleccionados debajo del título
          if (filtersSelected && Object.keys(filtersSelected).length > 0) {
            const filtersTextLines = Object.entries(filtersSelected).map(
              ([key, value]) => `${key}: ${value}`
            );

            context.font = "14px Roboto";
            context.fillStyle = "#000";

            context.fillText("Filtros seleccionados:", margin, contentHeight);
            contentHeight += 20; // Espacio para el header de filtros

            filtersTextLines.forEach((line) => {
              context.fillStyle = "#666";
              context.fillText(line, margin, contentHeight);
              contentHeight += 20; // Espacio para cada línea de filtro
            });

            contentHeight += 10; // Espacio adicional después de los filtros
          }

          // Dibuja el mapa debajo de los filtros seleccionados
          context.drawImage(canvas, margin, contentHeight, canvas.width, canvas.height);
          contentHeight += canvas.height + 40; // Añade espacio después del gráfico

          // Dibuja las referencias de capas debajo del mapa
          contentHeight = await drawLayerReferences(
            context,
            capasMapa,
            activeCapaIds,
            margin,
            contentHeight,
            canvas.width - margin * 2,
            lineHeight
          );

          // Dibuja la nota y el source debajo de la leyenda
          context.font = "14px Roboto";
          context.fillStyle = "#000";
          const maxWidth = canvas.width - margin * 2 - 40;

          if (note) {
            const noteHeight = wrapText(
              context,
              `Nota: ${note}`,
              margin,
              contentHeight,
              maxWidth,
              lineHeight
            );
            contentHeight += noteHeight + 10;
          }

          if (source) {
            const sourceHeight = wrapText(
              context,
              `Fuente: ${source}`,
              margin,
              contentHeight,
              maxWidth,
              lineHeight
            );
            contentHeight += sourceHeight + 10;
          }
        }
        sendAnalyticsEvent("downloaded_report", {
          report_title: name.trim(),
          file_type: format,
          source: name_indicator,
          module: 'Visualizador de indicadores',
        });

        const imgData = newCanvas.toDataURL(`image/${format}`);
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `${name}.${format}`;
        link.click();
      })
      .catch((error) => {
        console.error("Error al generar la imagen del mapa:", error);
      });
  } else {
    console.error("No se encontró el nodo para capturar");
  }
};